import { Skeleton } from "@particles/index"

import { ListItemStack } from "@molecules"

import { useCatalogItemModifiersQuery } from "@pages/Settings/SettingsCatalogs/SettingsCatalogsItemDetails/SettingsCatalogsItemDetails.store"
import ModifierGroupRow from "@pages/Settings/SettingsCatalogs/SettingsCatalogsItemDetails/components/ModifierGroupRow/ModifierGroupRow"

import styles from "./ItemOptions.module.scss"
import { ItemOptionsProps } from "./ItemOptions.types"

const MODIFIER_GROUP_DISPLAY_CAP = 50

export default function ItemOptions(props: ItemOptionsProps) {
    const { itemId } = props

    const modifiersQuery = useCatalogItemModifiersQuery(itemId)

    if (modifiersQuery.isLoading) {
        return (
            <div className={styles.optionsSkeleton}>
                <Skeleton height={20} width={96} />
                <Skeleton height={36} />
                <Skeleton height={36} />
                <Skeleton height={36} />
            </div>
        )
    }

    // Any failure means the platform is unreachable; hide the section quietly rather than surfacing an error.
    if (modifiersQuery.isError || !modifiersQuery.data) {
        return null
    }

    const groups = modifiersQuery.data.modifier_groups

    // Empty choice groups are already filtered out server-side, so an empty list means the item has no options.
    if (groups.length === 0) {
        return null
    }

    // Required groups past the cap stay visible: hiding one would misrepresent what the store demands at
    // checkout, and the line-item picker renders them the same way.
    const visibleGroups = groups.filter((group, index) => index < MODIFIER_GROUP_DISPLAY_CAP || group.is_required)
    const hiddenGroupCount = groups.length - visibleGroups.length

    return (
        <div className={styles.modifierGroups}>
            <h2 className={styles.sectionTitle}>Options</h2>
            <ListItemStack>
                {visibleGroups.map((group) => (
                    <ModifierGroupRow key={group.external_id} group={group} />
                ))}
            </ListItemStack>
            {hiddenGroupCount > 0 && (
                <span className={styles.hiddenGroupsNote}>
                    Showing {visibleGroups.length} of {groups.length} option groups.
                </span>
            )}
        </div>
    )
}
